import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import type { ServiceOrderLineStatus } from '../types/service-order.types';
import { BaseEntity } from './base.entity';
import { ItemEntity } from './item.entity';
import { MerchandiserEntity } from './merchandiser.entity';
import { ServiceOrderEntity } from './service-order.entity';

/**
 * PD-16797 — one thing to do on a service order ("door seal", "stuck depth
 * sensor"), optionally on a named unit, with the technician's record of it:
 * outcome, notes and photos (`trip_photo.fk_service_order_line__id`).
 *
 * Item, quantity and price are optional parts or labor, carried for billing
 * later; nothing reads them as a sale today.
 *
 * SM1 exposure: none. New table; SM1 never names it.
 */
@Entity('service_order_line')
@Index('idx_service_order_line_order', ['serviceOrderId'])
@Index('idx_service_order_line_merchandiser', ['merchandiserId'])
export class ServiceOrderLineEntity extends BaseEntity {
	@Column({ name: 'fk_service_order__id', type: 'int', unsigned: true })
	public serviceOrderId!: number;

	@Column({ name: 'sortOrder', type: 'int', unsigned: true, default: 0 })
	public sortOrder!: number;

	@Column({ name: 'description', type: 'varchar', length: 500 })
	public description!: string;

	/** The unit the line concerns, when it is about one. */
	@Column({
		name: 'fk_merchandiser__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public merchandiserId!: number | null;

	/** Optional part or labor item, for billing later. */
	@Column({ name: 'fk_item__id', type: 'int', unsigned: true, nullable: true })
	public itemId!: number | null;

	@Column({
		name: 'quantity',
		type: 'decimal',
		precision: 12,
		scale: 4,
		nullable: true,
	})
	public quantity!: string | null;

	@Column({
		name: 'price',
		type: 'decimal',
		precision: 12,
		scale: 4,
		nullable: true,
	})
	public price!: string | null;

	@Column({
		name: 'status',
		type: 'varchar',
		length: 16,
		default: "'pending'",
	})
	public status!: ServiceOrderLineStatus;

	/**
	 * Why the line was not done on a visit ("part on order", "no access").
	 * Kept when the line goes back to pending for the next visit, so the
	 * technician sees what stopped the last one.
	 */
	@Column({ name: 'notDoneReason', type: 'varchar', length: 500, nullable: true })
	public notDoneReason!: string | null;

	/** What the technician did or found ("replaced seal", "sensor reseated"). */
	@Column({ name: 'outcome', type: 'text', nullable: true })
	public outcome!: string | null;

	@Column({ name: 'notes', type: 'text', nullable: true })
	public notes!: string | null;

	/** The stop that recorded this line's outcome (done or not done). */
	@Column({
		name: 'fk_trip_stop__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public tripStopId!: number | null;

	/** When the outcome was recorded. */
	@Column({ name: 'workedDate', type: 'datetime', nullable: true })
	public workedDate!: Date | null;

	@ManyToOne(() => ServiceOrderEntity, (order) => order.lines)
	@JoinColumn({ name: 'fk_service_order__id' })
	public fkServiceOrder?: ServiceOrderEntity;

	@ManyToOne(() => MerchandiserEntity, { nullable: true })
	@JoinColumn({ name: 'fk_merchandiser__id' })
	public fkMerchandiser?: MerchandiserEntity | null;

	@ManyToOne(() => ItemEntity, { nullable: true })
	@JoinColumn({ name: 'fk_item__id' })
	public fkItem?: ItemEntity | null;
}
